import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { StockTransfer } from '@/lib/schemas';

export function useStockTransfers(params?: {
  status?: string;
  priority?: string;
  from_location_id?: string;
  to_location_id?: string;
  item_id?: string;
  search?: string;
  page?: number;
  page_size?: number;
}) {
  return useQuery<StockTransfer[], Error>({
    queryKey: ['stock-transfers', params],
    queryFn: () => api.getStockTransfers(params),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useStockTransfer(id: string) {
  return useQuery<StockTransfer | null, Error>({
    queryKey: ['stock-transfers', id],
    queryFn: () => api.getStockTransfer(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useStockTransferStats() {
  return useQuery({
    queryKey: ['stock-transfers', 'stats'],
    queryFn: () => api.getStockTransferStats(),
    staleTime: 60 * 1000, // 1 minute
  });
}

export function useCreateStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, Error, Omit<StockTransfer, 'transfer_id' | 'requested_at' | 'created_at' | 'status' | 'transfer_number'>>({
    mutationFn: (data) => api.createStockTransfer(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
    },
  });
}

export function useUpdateStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer | null, Error, { id: string; data: Partial<StockTransfer> }>({
    mutationFn: ({ id, data }) => api.updateStockTransfer(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
    },
  });
}

export function useApproveStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, Error, { id: string; notes?: string }>({
    mutationFn: ({ id, notes }) => api.approveStockTransfer(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers', 'stats'] });
    },
  });
}

export function usePickStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, Error, { id: string; notes?: string }>({
    mutationFn: ({ id, notes }) => api.pickStockTransfer(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useShipStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, Error, { id: string; trackingNumber?: string; carrier?: string; notes?: string }>({
    mutationFn: ({ id, trackingNumber, carrier, notes }) => 
      api.shipStockTransfer(id, trackingNumber, carrier, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
    },
  });
}

export function useReceiveStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, Error, { id: string; notes?: string; overrideDiscrepancy?: boolean }>({
    mutationFn: ({ id, notes, overrideDiscrepancy }) => 
      api.receiveStockTransfer(id, notes, overrideDiscrepancy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers', 'stats'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}

export function useRejectStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, Error, { id: string; notes?: string }>({
    mutationFn: ({ id, notes }) => api.rejectStockTransfer(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
    },
  });
}

export function useCancelStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, { id: string; reason: string }>({
    mutationFn: ({ id, reason }) => api.cancelStockTransfer(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['stock-transfers', 'stats'] });
    },
  });
}

export function useCompleteStockTransfer() {
  const queryClient = useQueryClient();
  return useMutation<StockTransfer, Error, { id: string; notes?: string }>({
    mutationFn: ({ id, notes }) => api.receiveStockTransfer(id, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stock-transfers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
    },
  });
}
